import { z } from "zod";
import { SPLIT_MIN_DAYS } from "./protocols";
/**
 * How this person trains — `GET`/`PUT /me/training-config` (`D115`).
 *
 * Six of the thirteen Session 1 items land here (goal, secondary goal, days,
 * how fixed those days are, session length, experience), and none of them had
 * a column before `0006`. The engine (T31) reads this row and nothing else to
 * decide the split, so a field that is wrong here is a plan that is wrong.
 *
 * ── `level` IS DERIVED, WHICH IS WHY IT IS ONLY ON THE OUTPUT ───────────────
 * The four levels come from `experience` by a rule the server owns. A client
 * that could `PUT` a level would be a second implementation of that rule, kept
 * in agreement by nothing.
 */
/** Blueprint §4.2 item 5. `return` is *"back after a break"*. */
export const GOALS = [
    "hypertrophy",
    "fat_loss",
    "recomposition",
    "strength",
    "general_health",
    "return",
    "event",
];
export const goal = z.enum(GOALS);
/**
 * Whether the week has fixed days.
 *
 * `mixed` is not a hedge: *"Monday and Wednesday always, the third whenever I
 * can"* is the common answer, and collapsing it into `variable` throws away
 * the two days the scheduler could actually use.
 */
export const DAYS_FIXED = ["fixed", "variable", "mixed"];
export const daysFixed = z.enum(DAYS_FIXED);
/**
 * The UPPER bound of the band the person chose, in minutes. `120` is the
 * open-ended top band — *"more than 90"* — and not a promise of two hours.
 */
export const SESSION_MINUTES = [30, 45, 60, 90, 120];
export const sessionMinutes = z.literal(SESSION_MINUTES);
/** Blueprint §4.2 item 9, the *"currently training"* follow-up. */
export const CONSISTENT_FOR = [
    "lt3m",
    "3to6m",
    "6mto1y",
    "1to3y",
    "gt3y",
];
/** The *"stopped"* follow-ups: how long ago, and for how long before that. */
export const STOPPED_AGO = ["lt3m", "3to12m", "1to3y", "gt3y"];
export const TRAINED_FOR = ["lt6m", "6mto2y", "gt2y"];
/**
 * The experience tree, as the three shapes it actually has.
 *
 * A discriminated union rather than a flat record of nullable follow-ups: the
 * three branches ask different questions, and a flat shape admits
 * `{ status: "never", stoppedAgo: "gt3y" }` — a row the engine would read and
 * silently believe.
 */
export const experience = z.discriminatedUnion("status", [
    z.strictObject({ status: z.literal("never") }),
    z.strictObject({
        consistentFor: z.enum(CONSISTENT_FOR),
        status: z.literal("current"),
    }),
    z.strictObject({
        status: z.literal("stopped"),
        stoppedAgo: z.enum(STOPPED_AGO),
        trainedFor: z.enum(TRAINED_FOR),
    }),
]);
/**
 * `intermediate_readapting` is a level and not a footnote (`D103`): somebody
 * who trained for two years and stopped for one is not a beginner and must not
 * be loaded like an intermediate either.
 */
export const TRAINING_LEVELS = [
    "beginner",
    "intermediate_readapting",
    "intermediate",
    "advanced",
];
export const trainingLevel = z.enum(TRAINING_LEVELS);
/**
 * The week shapes a person may choose for themselves (`D216`): `PLAN_SPLITS`
 * without `custom`, whose days the app cannot write yet — the trainer still
 * builds a custom week per proposal (`D213`).
 */
export const PREFERRED_SPLITS = [
    "full_body",
    "upper_lower",
    "ppl",
    "ppl_arms",
];
export const preferredSplit = z.enum(PREFERRED_SPLITS);
const trainingConfigShape = {
    /** `0` = Sunday … `6` = Saturday — `Date#getDay`'s numbering, so no client
     *  has to convert. Empty when the days are not fixed; see the checks. */
    daysFixed,
    daysPerWeek: z.int().min(2).max(6),
    /** Right or left handed, or neither — used for unilateral load asymmetry.
     *  Optional AND nullable: never asked, and asked-and-declined. */
    dominantSide: z.enum(["right", "left", "both"]).nullable().optional(),
    experience,
    fixedWeekdays: z.array(z.int().min(0).max(6)),
    primaryGoal: goal,
    /**
     * The week's shape the person chose (`D216`); `null` is "let the engine
     * choose" — `by_days` or the protocol's own split, as before. OPTIONAL on
     * input: an app older than the field omits it, and an omitted field keeps
     * what is stored rather than clearing it. Optional on output too, so an
     * app survives an older server (`D172`).
     */
    preferredSplit: preferredSplit.nullable().optional(),
    /** NULL is *"none"* — the onboarding offers it explicitly (`§4`). */
    secondaryGoal: goal.nullable(),
    sessionMinutes,
    /**
     * `true` = *"I cannot go over"*, `false` = *"I would rather not"*. The
     * engine treats the first as a constraint on the item count and the second
     * as a preference it may exceed by one exercise.
     */
    sessionMinutesIsHardLimit: z.boolean(),
};
export const trainingConfigInput = z
    .strictObject(trainingConfigShape)
    .refine((value) => new Set(value.fixedWeekdays).size === value.fixedWeekdays.length, { error: "fixedWeekdays must not repeat a day", path: ["fixedWeekdays"] })
    .refine((value) => value.fixedWeekdays.length <= value.daysPerWeek, {
    error: "fixedWeekdays must not name more days than daysPerWeek",
    path: ["fixedWeekdays"],
})
    .refine((value) => value.daysFixed !== "variable" || value.fixedWeekdays.length === 0, {
    // A variable week with three named days is two answers that contradict
    // each other, and the scheduler would use the days.
    error: "fixedWeekdays must be empty when daysFixed is variable",
    path: ["fixedWeekdays"],
})
    .refine((value) => value.secondaryGoal === null || value.secondaryGoal !== value.primaryGoal, {
    error: "secondaryGoal must differ from primaryGoal",
    path: ["secondaryGoal"],
})
    .refine((value) => value.preferredSplit === undefined ||
    value.preferredSplit === null ||
    value.daysPerWeek >= SPLIT_MIN_DAYS[value.preferredSplit], {
    // PPL in two days trains each movement less than once a week; the
    // engine refuses to build it (`prescription.split_needs_days`), so the
    // preference is refused where it is written.
    error: "preferredSplit needs more days than daysPerWeek",
    path: ["preferredSplit"],
});
export const trainingConfigOutput = z.object({
    ...trainingConfigShape,
    level: trainingLevel,
    updatedAt: z.iso.datetime(),
});
//# sourceMappingURL=training-config.js.map